<!--
  AddNewOrderCard -- the SA switch that offers the "Add New Order" button on
  the distributor Orders page and opens its page (card #645).

  Platform setting `add_new_order_enabled`, DEFAULT OFF on every environment:
  an unset row, a failed read and a malformed answer all present as OFF.
  OFF hides the button and sends a direct visit to the page back to the
  Orders list. It does not touch the Client Portal's own new-order flow or
  the storefront checkout. Kept as its own component so the Platform Settings
  page only mounts it; the read and the write live here.
-->
<template>
    <div class="anoc-card" data-testid="sa-setting-add-new-order">
        <div class="anoc-card__body">
            <div class="anoc-card__copy">
                <label class="anoc-card__label" for="sa-setting-add-new-order-switch">
                    Add New Order
                </label>
                <p class="anoc-card__help">
                    When on, the distributor Orders page shows the Add New
                    Order button, and the page it opens is available to
                    Owners, Builders and Sales Executives who can place
                    orders. Off by default.
                </p>
                <p class="anoc-card__help" data-testid="sa-setting-add-new-order-note">
                    Turning this off hides the button and sends anyone who
                    opens the page directly back to the Orders list. It does
                    not change the Client Portal's own new-order page or
                    anything a shopper does at checkout.
                </p>
            </div>

            <label class="anoc-toggle" data-testid="sa-setting-add-new-order-toggle">
                <input
                    id="sa-setting-add-new-order-switch"
                    v-model="enabled"
                    type="checkbox"
                    class="anoc-toggle__input"
                    data-testid="sa-setting-add-new-order-switch"
                    :disabled="saving"
                    @change="onToggle"
                />
                <span class="anoc-toggle__track" aria-hidden="true">
                    <span class="anoc-toggle__thumb"></span>
                </span>
                <span class="anoc-toggle__state" data-testid="sa-setting-add-new-order-state">{{ enabled ? "On" : "Off" }}</span>
            </label>
        </div>

        <p
            v-if="errorMsg"
            class="anoc-card__error"
            data-testid="sa-setting-add-new-order-error"
        >
            {{ errorMsg }}
        </p>
    </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import axios from "@/axios";
import { invalidateAddNewOrderEnabled } from "@/composables/useAddNewOrderEnabled";

const URL = "/api/platform-settings/add-new-order-enabled";

// Default OFF, also when the read fails (never present the tools as on
// without an answer from the server).
const enabled = ref(false);
const saving = ref(false);
const errorMsg = ref("");

async function load() {
    errorMsg.value = "";
    try {
        const resp = await axios.get(URL);
        enabled.value =
            typeof resp?.data?.value === "boolean" ? resp.data.value : false;
    } catch (e) {
        enabled.value = false;
        errorMsg.value = "Could not load the Add New Order setting.";
    }
}

async function onToggle() {
    const desired = enabled.value;
    saving.value = true;
    errorMsg.value = "";
    try {
        const resp = await axios.patch(URL, { value: desired });
        enabled.value = resp?.data?.value === true;
        // The Orders page in this tab must re-read, not trust an older answer.
        invalidateAddNewOrderEnabled();
    } catch (e) {
        enabled.value = !desired;
        errorMsg.value =
            e?.response?.status === 403
                ? "Permission denied. Only Super Admins can change this setting."
                : "Could not save this setting.";
    } finally {
        saving.value = false;
    }
}

onMounted(load);
</script>

<style scoped>
.anoc-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 16px 20px;
    margin-bottom: 24px;
}
.anoc-card__body {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
}
.anoc-card__copy {
    min-width: 0;
}
.anoc-card__label {
    display: block;
    font-weight: 600;
    color: #1f2937;
    font-size: 15px;
    margin-bottom: 4px;
    cursor: pointer;
}
.anoc-card__help {
    margin: 0;
    color: #4b5563;
    font-size: 13px;
    line-height: 1.5;
    max-width: 460px;
}
.anoc-card__error {
    margin: 12px 0 0;
    color: #b91c1c;
    font-size: 13px;
}
.anoc-toggle {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    flex: 0 0 auto;
}
.anoc-toggle__input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
}
.anoc-toggle__track {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;
    border-radius: 999px;
    background: #cbd2dc;
    transition: background 0.15s ease;
    flex: 0 0 auto;
}
.anoc-toggle__thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(28, 36, 48, 0.25);
    transition: transform 0.15s ease;
}
.anoc-toggle__input:checked + .anoc-toggle__track {
    background: #2f6df6;
}
.anoc-toggle__input:checked + .anoc-toggle__track .anoc-toggle__thumb {
    transform: translateX(18px);
}
.anoc-toggle__input:focus-visible + .anoc-toggle__track {
    outline: 2px solid #2f6df6;
    outline-offset: 2px;
}
.anoc-toggle__state {
    font-size: 13px;
    color: #5b6572;
}
</style>
